import { CharityVettingUrlType } from 'features/charity-vetting/types/charityVettingTypes';
import { z } from 'zod';

import plMsg from './messages';

import type {
  PageSchemaFactory,
  PageSchemaMap,
} from 'features/charity-vetting/common-flow/validation/pageSchemaRegistry';

/**
 * Per-page Zod schemas for the PL vetting flow. KRS and NIP are both required
 * 10-digit numbers (stored as number|null, validated as their string form).
 */

const wrap = (charityOnboarding: z.ZodTypeAny) => z.looseObject({ charityOnboarding });

const TEN_DIGITS = /^\d{10}$/;

const toStr = (value: unknown) =>
  typeof value === 'string' || typeof value === 'number' ? String(value) : '';

type TenDigitMessages = {
  required: (typeof plMsg)[keyof typeof plMsg];
  invalid: (typeof plMsg)[keyof typeof plMsg];
};

const tenDigitFieldSchema =
  (field: 'krs' | 'nip', messages: TenDigitMessages): PageSchemaFactory =>
  (formatMessage) =>
    wrap(
      z.looseObject({ [field]: z.unknown().optional() }).superRefine((data, ctx) => {
        const value = toStr(data[field]);
        if (!value) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: formatMessage(messages.required),
            path: [field],
          });
          return;
        }
        if (!TEN_DIGITS.test(value)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: formatMessage(messages.invalid),
            path: [field],
          });
        }
      }),
    );

const krsPageSchema = tenDigitFieldSchema('krs', {
  required: plMsg.charityVettingKrsRequiredPL,
  invalid: plMsg.charityVettingKrsInvalidPL,
});

const nipPageSchema = tenDigitFieldSchema('nip', {
  required: plMsg.charityVettingNipRequiredPL,
  invalid: plMsg.charityVettingNipInvalidPL,
});

const organisationTypePageSchema: PageSchemaFactory = () =>
  wrap(z.looseObject({ charityType: z.string().min(1) }));

const publicBenefitPageSchema: PageSchemaFactory = () =>
  wrap(z.looseObject({ isPublicBenefitOrg: z.boolean() }));

export const plPageSchemas: PageSchemaMap = {
  [CharityVettingUrlType.KRS]: krsPageSchema,
  [CharityVettingUrlType.NIP]: nipPageSchema,
  [CharityVettingUrlType.REGISTRATION_DETAILS]: organisationTypePageSchema,
  [CharityVettingUrlType.PUBLIC_BENEFIT]: publicBenefitPageSchema,
};
